import * as React from 'react';

import { Field, FieldProps, useFormikContext } from 'formik';
import { v4 } from 'uuid';

import Table, { ColumnProps } from 'antd/lib/table';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';

import { DeleteOutlined } from '@ant-design/icons';

import { List } from '@common/typescript/objects/List';
import { Nullable } from '@common/typescript/objects/Nullable';

import { FormikInput } from '@common/react/components/Forms/FormikInput/FormikInput';
import { AlertMessage, AlertType } from '@common/react/components/UI/AlertMessage/AlertMessage';
import TableBuilder from '@common/react/utils/Helpers/TableBuilder';

import { Card, ListComponent, ItemsList } from '@app/components/UI/Cards/Card';
import { ServiceType } from '@app/objects/Pet';
import { PriceType, Price, PriceKind } from '@app/objects/Price';
import { useRequest } from '@app/hooks/useRequest';
import { useModal } from '@app/hooks/Editors/useModal';
import { ViewMode } from '@app/components/Utils/Utils';
import { FormValues } from '@app/components/Pages/ClinicPetEditor/types';
import {
	SpecialServiceDialog,
} from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/SpecialServices/SpecialServiceDialog';
import { BaseSectionProps } from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/ViewContent';
import {
	getAdditionalPrice,
	getDisableModal,
	getPetPrice,
} from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/utils';
import { NumberField } from '@app/components/UI/NumberField/NumberField';
import {
	ServiceIndexProvider,
} from '@app/components/Pages/PetEditor/Components/Sections/SpecialServiceSection/ServiceIndexProvider';
import { useCrematory } from '@app/hooks/Crematory/useCrematory';
import {
	expendedProps,
} from '@app/components/Pages/PetEditor/Components/Sections/SpecialServiceSection/SpecialServiceList';
import { SpecialServiceType } from '@app/objects/SpecialService';
import { PetPrice } from '@app/objects/PetPrice';

function getSpecialServices(services: Array<PetPrice>): Array<PetPrice> {
	return services.filter((q: PetPrice) => q.price?.specialServiceId !== null)
		.filter((q: PetPrice) => q.price?.specialService?.specialServiceType !== SpecialServiceType.KitService)
		.filter((q: PetPrice) => !q.removed);
}

function getPresets(services: Array<PetPrice>): Array<PetPrice> {
	return services.filter((q: PetPrice) => q.price?.priceKind === PriceKind.Preset).filter((item: PetPrice) => !item.removed);
}

function getSpecialServicesList(pet: FormValues): Array<ItemsList> {
	const services = getSpecialServices(pet.services);

	return getPetPrice(services);
}

const servicesColumns = (showPricesToClinic?: boolean): Array<ColumnProps<PetPrice>> => TableBuilder.shape<PetPrice>()
	.addColumn({
		title: 'Quantity',
		dataIndex: 'count',
		width: '12ch',
		align: 'right',
		render: (count: number, record: PetPrice) => (
			<Field name="services">
				{(fieldProps: FieldProps<FormValues>) => (
					<FormikInput
						fieldProps={fieldProps}
						containerClassName=""
						render={({ field, form }: FieldProps<Array<PetPrice>, FormValues>) => {
							const isPreset = Boolean(record.parentId);

							if (isPreset) {
								return (
									<Typography.Text>
										{record.count}
									</Typography.Text>
								);
							}

							return (
								<NumberField
									{...field}
									min={0}
									style={{
										textAlign: 'right',
									}}
									value={record.count}
									onChange={(count: number) => {
										const services = field.value.map((item: PetPrice) => {
											if (item.clientId === record.clientId) {
												const batchCount: number = item.price?.batchCount ?? 0;
												const batchPrice: number = item.price?.batchPrice ?? 0;
												const perItem: number = item.price?.value ?? 0;

												const value = batchPrice + Math.max(count - batchCount, 0) * perItem;
												const extra = count <= batchCount ? 0 : (count - batchCount) * perItem;

												return {
													...item,
													count,
													value,
													extra,
												};
											}

											return item;
										});
										form.setFieldValue(field.name as keyof FormValues, services, false);
									}}
								/>
							);
						}}
					/>
				)}
			</Field>
		),
	})
	.addColumn({
		title: 'Name',
		dataIndex: 'name',
		render: (name: string, record: PetPrice) => {
			const batchCount: Nullable<number> = record.price?.batchCount ?? null;
			const batchPrice: Nullable<number> = record.price?.batchPrice ?? null;
			const withBatch = batchCount !== null && batchPrice !== null;

			const batchDescription = withBatch ? `${batchCount} at $${batchPrice}` : null;
			const additionalDescription = `Each additional - $${record.price?.value}`;
			const isPreset = Boolean(record.parentId);
			const displayPrice = Boolean(showPricesToClinic && withBatch && batchCount! > 0);
			const displayBatch = Boolean(showPricesToClinic && record.price);

			return (
				<Space
					direction="vertical"
					size={2}
				>
					<Space
						direction="vertical"
						size={0}
					>
						<Typography.Text>{name}</Typography.Text>

						{
							isPreset
								? (
									<Typography.Text type="secondary">
										{record.parent?.name ?? 'Preset'}
									</Typography.Text>
								) : null
						}
					</Space>

					<Space
						size={0}
						direction="vertical"
					>
						{
							displayPrice && !isPreset
								? (
									<Typography.Text>
										{batchDescription}
									</Typography.Text>
								) : null
						}
						{
							displayBatch && !isPreset
								? (
									<Typography.Text type="secondary">
										{additionalDescription}
									</Typography.Text>
								) : null
						}
					</Space>
				</Space>
			);
		},
	})
	.addColumn({
		title: 'Notes',
		dataIndex: 'note',
		render: (_, record: PetPrice) => (
			<ServiceIndexProvider clientId={record.clientId}>
				{
					(index: Nullable<number>) => {
						if (index === null) return <div>-</div>;

						return (
							<Field name={`services.${index}.note`}>
								{
									({ field, form, meta }) => (
										<FormikInput
											fieldProps={{ field, form, meta }}
											inputProps={{ disabled: !record.price?.specialService?.hasNote || record.done }}
											containerClassName=""
										/>
									)
								}
							</Field>
						);
					}
				}
			</ServiceIndexProvider>
		),
	})
	.addColumn({
		title: '',
		dataIndex: 'actions',
		render: (_, record: PetPrice) => (
			<Field name="services">
				{({ field, form }: FieldProps<Array<PetPrice>, FormValues>) => (
					<Button
						title="Delete"
						type="text"
						danger
						icon={<DeleteOutlined />}
						onClick={() => {
							let values: Array<PetPrice> = [];

							if (record.id <= 0) {
								values = field.value.filter((q: PetPrice) => q.clientId !== record.clientId);
							} else {
								values = field.value.map((q: PetPrice) => {
									if (q.id === record.id) {
										return {
											...q,
											removed: true,
										};
									}

									return q;
								});
							}

							form.setFieldValue(field.name as keyof FormValues, values, false);
						}}
						disabled={Boolean(record.parentId)}
					/>
				)}
			</Field>
		),
	})
	.build();

interface DefaultService {
	clientId: string;
	name: string;

	count: number;
	value: number;
	extra: number;

	priceId: number;
	price: Price;

	batchCount: number;
	batchPrice: number;

	note: string;
}

interface DefaultServicesProps {
	id: number;
	crematoryId: number;
	serviceType: number;

	onChange: (field: string, list: Array<PetPrice>, validate: boolean) => void;
	onLoad: (value: boolean) => void;
	onError: (error: Nullable<string>) => void;
	onRetry: (retry: () => void) => void;
}

interface AvailablePriceListParams {
	priceType: PriceType;
	serviceType: ServiceType;

	withSpecialService: boolean;
	withMyClinic: boolean;
	specialService: boolean;
	isDefault: boolean;

	count: number;
}

interface RequestParams {
	clinicId: Nullable<number>;
	priceType: PriceType;
	serviceType?: ServiceType;
	withSpecialService: boolean;
	count: number;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const DefaultServicesTracker: React.VFC<DefaultServicesProps> = (props: DefaultServicesProps) => {
	const request = useRequest<List<Price>, AvailablePriceListParams>('availablePriceList', undefined, { requestOnMount: false });
	const prevCrematoryId = React.useRef<number>(props.id > 0 ? props.crematoryId : -1);
	const prevServiceType = React.useRef<Nullable<ServiceType>>(props.id > 0 ? props.serviceType : null);

	React.useEffect(() => {
		if (props.crematoryId === -1 || props.serviceType === undefined) return;

		if (props.crematoryId !== prevCrematoryId.current || props.serviceType !== prevServiceType.current) {
			prevCrematoryId.current = props.crematoryId;
			prevServiceType.current = props.serviceType;

			const params = {
				priceType: PriceType.Wholesale,
				serviceType: props.serviceType,

				withSpecialService: true,
				withMyClinic: true,
				specialService: true,
				isDefault: true,

				count: 1000,
			};

			props.onRetry(() => request.reload(params).catch(() => undefined));
			request.reload(params).catch(() => undefined);
		}
	}, [props.crematoryId, props.serviceType]);

	React.useEffect(() => {
		if (request.item !== null) {
			const services: Array<PetPrice> = (request.item?.list ?? [])
				.map((item: Price) => ({
					clientId: v4(),
					name: item.specialService?.name ?? 'Special Service',

					count: item.batchCount || 1,
					value: item.batchCount > 1 ? item.value : item.batchPrice,
					extra: item.batchCount > 0 ? 0 : item.value,

					priceId: item.id,
					price: item,

					batchCount: item.batchCount,
					batchPrice: item.batchPrice,

					pickupService: null,
					pickupServiceId: null,

					engravingId: null,
					engraving: null,

					node: null,
					nodeId: null,

					note: item.specialService?.defaultNote ?? '',
				})).map((item: DefaultService) => ({
					id: -1,
					clientId: item.clientId,
					name: item.name,

					petId: props.id,
					pet: null,

					priceId: item.priceId,
					price: item.price,

					count: item.count,
					completedCount: 0,
					done: false,

					value: item.value,
					extra: item.extra,
					tax: 0,
					discount: 0,

					editor: null,
					editorId: null,

					pickupService: null,
					pickupServiceId: null,

					batchCount: item.batchCount,
					batchPrice: item.batchPrice,

					engravingId: null,
					engraving: null,

					node: null,
					nodeId: null,

					note: item.note ?? '',
				}));

			props.onChange('services', services, false);
		}
	}, [request.item, props.onChange]);

	React.useEffect(() => {
		props.onLoad(request.loading);
	}, [request.loading, props.onLoad]);

	React.useEffect(() => {
		props.onError(request.error);
	}, [request.error, props.onError]);

	return null;
};

export const SpecialServices: React.FC<BaseSectionProps> = (props: BaseSectionProps) => {
	const { values, setFieldValue } = useFormikContext<FormValues>();
	const modal = useModal(false);
	const crematory = useCrematory();
	const showPricesToClinic = crematory?.showPricesToClinic;
	const [loading, setLoading] = React.useState<boolean>(() => false);
	const [error, setError] = React.useState<Nullable<string>>(() => null);
	const [retry, setRetry] = React.useState<() => void>(() => () => undefined);
	const [prices, setPrices] = React.useState<Array<Price>>([]);
	const request = useRequest<List<Price>, RequestParams>('priceList', undefined, { requestOnMount: false });

	const requestParams = React.useMemo(() => ({
		clinicId: null,
		priceType: PriceType.Wholesale,
		serviceType: values.serviceType,
		withSpecialService: true,
		count: 100,
	}), [values.serviceType]);

	const services = getSpecialServices(values.services);
	const presets = getPresets(values.services);

	React.useEffect(() => {
		if (!requestParams.serviceType) return;

		request.reload(requestParams)
			.then((response: List<Price> | void) => {
				if (!response) return;

				const priceList = response.list.filter((price: Price) => price.specialServiceId)
					.sort((a: Price, b: Price) => a.to - b.to);

				setPrices(priceList);
			});
	}, [requestParams]);

	return (
		<Card
			name="Special Services and Memorial Items"
			mode={props.mode}
			fullWidth

			withBtn={props.mode === ViewMode.edit}
			onClick={modal.open}
			disabled={getDisableModal(values.serviceType)}

			editContent={(
				<>
					<DefaultServicesTracker
						id={values.id}
						crematoryId={props.crematoryId}
						serviceType={values.serviceType!}

						onChange={setFieldValue}
						onLoad={setLoading}
						onError={setError}
						onRetry={(value) => setRetry(() => value)}
					/>
					<Table
						columns={servicesColumns(showPricesToClinic)}
						dataSource={services}
						pagination={{ hideOnSinglePage: true }}
						rowKey={(record: PetPrice) => (values.id > 0 ? record.id.toString() : record.clientId)}
						loading={loading}
						expandable={expendedProps(services?.filter((q: PetPrice) => !q.removed) ?? [], prices)}
					/>
					{
						showPricesToClinic
							? (
								<div className="additional-amount">
									<span>
										Additional amount: ${getAdditionalPrice(services).toFixed(2)}
									</span>
								</div>
							) : null
					}
					<AlertMessage type={AlertType.Danger} message={error} />
					{
						error ? (
							<Button
								loading={loading}
								onClick={retry}
								type="link"
							>
								Retry
							</Button>
						) : null
					}
					<SpecialServiceDialog
						petId={values.id}
						serviceType={values.serviceType!}
						priceType={PriceType.Wholesale}

						value={services}
						onChange={(values: Array<PetPrice>) => setFieldValue('services', [...values, ...presets], false)}

						state={modal.state}
						close={modal.close}
					/>
				</>
			)}

			viewContent={(
				<ListComponent
					list={getSpecialServicesList(values)}
					placeholder="There are no special services or memorabilia items"
				/>
			)}
		/>
	);
};
